import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Button,
  Group,
  Stack,
  Text,
  Box,
  Radio,
  Checkbox,
  LoadingOverlay,
  Alert,
  Badge,
  ScrollArea,
  Divider,
  Card,
  Select,
  TextInput,
  Tooltip,
  Collapse,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  Download,
  AlertCircle,
  CheckCircle,
  Filter,
  Mail,
  Lock,
  ChevronDown,
  ChevronUp,
} from 'react-feather';
import {
  exportsApi,
  type ExportEntityType,
  type ExportContextType,
  type ExportColumnDefinition,
  type ExportColumnCategory,
  type ExportPreset,
  type DnmGroupingsValidation,
  getEntityLabel,
} from '../../../services/api/exports';
import type { ColumnConfig } from '../../../hooks/useColumnConfig';
import { useAuthStore } from '../../../stores/auth';
import classes from './ExportModal.module.css';
import { exportTitle, ignoredFiltersNotice, type ExportScope } from './filterSummary';

/**
 * Map a grid column to its backend export key.
 * If exportKey is explicitly null, the column is not exportable.
 * If exportKey is undefined, fall back to the column id.
 */
function getExportKey(col: ColumnConfig): string | null {
  if (col.exportKey === null) return null;
  return col.exportKey ?? col.id;
}

/**
 * Get exportable column keys from grid column configs.
 * Filters out non-exportable columns (exportKey === null) and returns backend export keys.
 */
function getExportKeysFromGrid(gridColumns: ColumnConfig[]): string[] {
  return gridColumns
    .map(getExportKey)
    .filter((key): key is string => key !== null);
}

/**
 * Get exportable column keys for currently visible grid columns.
 * Preserves the visible order from the grid.
 */
function getVisibleExportKeys(gridColumns: ColumnConfig[], visibleIds: string[]): string[] {
  return visibleIds
    .map(id => gridColumns.find(c => c.id === id))
    .filter((col): col is ColumnConfig => col !== undefined)
    .map(getExportKey)
    .filter((key): key is string => key !== null);
}

/**
 * "All columns" = every deliverable field for this entity/view: the UNION of the
 * grid's export keys and the backend's available column keys. This handles both
 * directions the audit found (MED-1234): grid ⊂ backend (e.g. participants, where
 * the grid shows fewer than the 44 exportable columns) and grid ⊃ backend (survey
 * tabs, whose grids carry dynamic q_<question_id> columns the static backend list
 * lacks). Deduped; order is backend-first, then any grid-only keys.
 */
function getAllColumnKeys(
  gridColumns: ColumnConfig[] | undefined,
  backendColumns: { key: string }[],
): string[] {
  const gridKeys = gridColumns ? getExportKeysFromGrid(gridColumns) : [];
  return Array.from(new Set([...backendColumns.map(c => c.key), ...gridKeys]));
}

// Stable empty array references to prevent useEffect infinite loops
const EMPTY_STRING_ARRAY: string[] = [];
const EMPTY_FILTER_SUMMARY: { label: string; value: string }[] = [];
const EMPTY_COLUMNS: ExportColumnDefinition[] = [];

/** Columns that cannot be unchecked when using the matching tool preset */
const MATCHING_TOOL_REQUIRED_COLUMNS = new Set(['email', 'available_time_slots']);

interface AvailabilityValidation {
  total_participants: number;
  missing_availability_count: number;
  missing_availability_participants: { first_name: string; last_name: string }[];
}

interface ExportModalProps {
  opened: boolean;
  onClose: () => void;
  entityType: ExportEntityType;
  contextType?: ExportContextType;
  contextId?: string;
  /** Current filter/search state from the table */
  filterContext?: Record<string, unknown>;
  /**
   * What the export covers whatever the filters are, such as the selected
   * cohorts or the survey kind. "Include all records" keeps it (MDF-208).
   */
  scopeContext?: Record<string, unknown>;
  /** Keys of currently visible columns in the table */
  currentViewColumns?: string[];
  /**
   * Full grid column configuration from the parent tab.
   * When provided, "All columns" and "Match current view" modes use the grid schema
   * as the source of truth instead of the backend column definitions.
   */
  gridColumns?: ColumnConfig[];
  /**
   * The filters that are set and that the export applies. The "Active
   * Filters" box shows only when this list has an item (MDF-208).
   */
  filterSummary?: { label: string; value: string }[];
  /** Labels of the screen filters that the export ignores (MDF-208). */
  ignoredFilters?: string[];
  /** Total record count to display */
  recordCount?: number;
  /** Title noun when the entity type's own label is wrong for this tab (MDF-253). */
  entityLabel?: string;
  /** Offer only the grid's columns; the entity's backend list does not apply to this tab (MDF-253). */
  gridColumnsOnly?: boolean;
}

type ExportMode = 'all_columns' | 'current_view' | 'matching_tool' | 'custom';

export function ExportModal({
  opened,
  onClose,
  entityType,
  contextType = 'global',
  contextId,
  filterContext,
  scopeContext,
  currentViewColumns = EMPTY_STRING_ARRAY,
  gridColumns,
  filterSummary = EMPTY_FILTER_SUMMARY,
  ignoredFilters = EMPTY_STRING_ARRAY,
  recordCount,
  entityLabel: entityLabelOverride,
  gridColumnsOnly = false,
}: ExportModalProps) {
  // Get user email from auth store
  const { user } = useAuthStore();
  const userEmail = user?.email;

  const [columnsLoading, setColumnsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [backendColumns, setColumns] = useState<ExportColumnDefinition[]>([]);
  const columns = gridColumnsOnly ? EMPTY_COLUMNS : backendColumns;
  const [categories, setCategories] = useState<{ key: ExportColumnCategory; label: string }[]>([]);
  const [presets, setPresets] = useState<Record<string, ExportPreset> | undefined>();

  const [exportMode, setExportMode] = useState<ExportMode>('all_columns');
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());
  const [selectedFormat, setSelectedFormat] = useState<string>('csv');
  const [includeAllRecords, setIncludeAllRecords] = useState(false);
  // The scope always applies; "Include all records" drops only the filters (MDF-208).
  const dropFilters = includeAllRecords && filterSummary.length > 0;
  const mergedContext = { ...scopeContext, ...(dropFilters ? undefined : filterContext) };
  const requestFilterContext = Object.keys(mergedContext).length > 0 ? mergedContext : undefined;
  const [pendingCount, setPendingCount] = useState(0);
  const [maxPending, setMaxPending] = useState(5);

  // Availability validation for matching tool preset
  const [availabilityValidation, setAvailabilityValidation] = useState<AvailabilityValidation | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [missingListExpanded, setMissingListExpanded] = useState(false);
  const [includeMissingAvailability, setIncludeMissingAvailability] = useState(false);

  // DNM groupings for matching tool preset
  const [populateDnmGroupings, setPopulateDnmGroupings] = useState(true);
  const [dnmValidation, setDnmValidation] = useState<DnmGroupingsValidation | null>(null);
  const [dnmValidationLoading, setDnmValidationLoading] = useState(false);
  const [dnmMissingListExpanded, setDnmMissingListExpanded] = useState(false);

  // Fetch available columns when modal opens
  useEffect(() => {
    if (opened) {
      fetchColumns();
      fetchPendingCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch functions are stable, only re-run when modal opens or entity changes
  }, [opened, entityType]);

  // Update selected columns when mode changes
  useEffect(() => {
    if (exportMode === 'all_columns') {
      // MED-1234: "All columns" = the full deliverable set (grid ∪ backend),
      // so it can never be a confusing subset of what Custom selection offers.
      setSelectedColumns(new Set(getAllColumnKeys(gridColumns, columns)));
    } else if (exportMode === 'current_view') {
      if (gridColumns) {
        // Map visible grid columns to export keys (MED-771)
        setSelectedColumns(new Set(getVisibleExportKeys(gridColumns, currentViewColumns)));
      } else {
        const validColumns = columns.filter(c => currentViewColumns.includes(c.key));
        setSelectedColumns(new Set(validColumns.map(c => c.key)));
      }
    } else if (exportMode === 'matching_tool' && presets?.matching_tool) {
      // Pre-populate with matching tool columns
      setSelectedColumns(new Set(presets.matching_tool.columns));
    }
    // For 'custom' mode, keep current selection
  }, [exportMode, columns, currentViewColumns, gridColumns, presets]);

  const fetchColumns = async () => {
    setColumnsLoading(true);
    try {
      // MDF-214: the cohorts in scope add their custom fields to the column list.
      const cohortIds = contextType === 'cohort' && contextId
        ? [contextId]
        : (({ ...scopeContext, ...filterContext }.cohortIds as string[] | undefined) ?? []);
      const response = await exportsApi.getAvailableColumns(entityType, cohortIds);
      setColumns(response.columns);
      setCategories(response.categories);
      setPresets(response.presets);
      // Default to all columns selected
      setSelectedColumns(new Set(response.columns.map(c => c.key)));
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load export columns',
        color: 'red',
      });
    } finally {
      setColumnsLoading(false);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const response = await exportsApi.getPendingCount();
      setPendingCount(response.pending_count);
      setMaxPending(response.max_pending);
    } catch {
      // Silently fail
    }
  };

  // Fetch availability validation when matching tool mode is selected
  useEffect(() => {
    if (exportMode === 'matching_tool' && entityType === 'participants' && contextId) {
      fetchAvailabilityValidation();
    } else {
      setAvailabilityValidation(null);
      setMissingListExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when the mode or the record scope changes
  }, [exportMode, entityType, contextId, dropFilters]);

  const fetchAvailabilityValidation = async () => {
    if (!contextId) return;
    setAvailabilityLoading(true);
    try {
      const response = await exportsApi.validateMatchingTool(contextType, contextId, requestFilterContext);
      setAvailabilityValidation(response);
    } catch {
      // Silently fail - validation is informational
    } finally {
      setAvailabilityLoading(false);
    }
  };

  // Fetch DNM groupings validation when matching tool mode + checkbox checked
  useEffect(() => {
    if (exportMode === 'matching_tool' && populateDnmGroupings && entityType === 'participants' && contextId) {
      fetchDnmValidation();
    } else {
      setDnmValidation(null);
      setDnmMissingListExpanded(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run when the mode, the checkbox or the record scope changes
  }, [exportMode, populateDnmGroupings, entityType, contextId, dropFilters]);

  const fetchDnmValidation = async () => {
    if (!contextId) return;
    setDnmValidationLoading(true);
    try {
      const response = await exportsApi.validateDnmGroupings(contextType, contextId, requestFilterContext);
      setDnmValidation(response);
    } catch {
      // Silently fail - validation is informational
    } finally {
      setDnmValidationLoading(false);
    }
  };

  // Full set of selectable columns for Custom selection (MED-1234): the backend
  // registry UNION any grid-only exportable columns. Grid surfaces can expose
  // columns the static backend list lacks — notably the dynamic
  // q_<question_id> survey columns — so Custom selection must offer them too,
  // not just "All columns"/"Match current view". Grid-only keys are grouped
  // under a synthetic category. Mirrors getAllColumnKeys' union.
  const availableColumns = useMemo<ExportColumnDefinition[]>(() => {
    if (!gridColumns) return columns;
    const seen = new Set(columns.map(c => c.key));
    const gridOnly: ExportColumnDefinition[] = [];
    for (const gc of gridColumns) {
      const key = getExportKey(gc);
      if (key === null || seen.has(key)) continue;
      seen.add(key);
      gridOnly.push({
        key,
        label: gc.label,
        category: key.startsWith('q_') ? 'survey_questions' : 'additional',
        field: key,
      });
    }
    return gridOnly.length > 0 ? [...columns, ...gridOnly] : columns;
  }, [columns, gridColumns]);

  // Group columns by category
  const columnsByCategory = useMemo(() => {
    const grouped: Record<string, ExportColumnDefinition[]> = {};
    for (const column of availableColumns) {
      const category = column.category || 'basic';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(column);
    }
    return grouped;
  }, [availableColumns]);

  // Get category label (backend categories + synthetic grid-only categories)
  const getCategoryLabel = (key: string): string => {
    const category = categories.find(c => c.key === key);
    if (category?.label) return category.label;
    if (key === 'survey_questions') return 'Survey Questions';
    if (key === 'additional') return 'Additional Columns';
    return key;
  };

  // Check if a column is locked (required and cannot be unchecked)
  const isColumnLocked = (key: string): boolean => {
    return exportMode === 'matching_tool' && MATCHING_TOOL_REQUIRED_COLUMNS.has(key);
  };

  // Toggle column selection
  const toggleColumn = (key: string) => {
    // Prevent unchecking required columns in matching tool mode
    if (isColumnLocked(key)) return;

    const newSelected = new Set(selectedColumns);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedColumns(newSelected);
    // Switch to custom mode if user manually changes selection from all_columns or current_view
    // BUT keep matching_tool mode if that's selected (per AC4)
    if (exportMode === 'all_columns' || exportMode === 'current_view') {
      setExportMode('custom');
    }
  };

  // Toggle all columns
  const toggleAll = () => {
    if (selectedColumns.size === availableColumns.length) {
      // When deselecting all in matching_tool mode, keep required columns checked
      if (exportMode === 'matching_tool') {
        setSelectedColumns(new Set(MATCHING_TOOL_REQUIRED_COLUMNS));
      } else {
        setSelectedColumns(new Set());
      }
    } else {
      setSelectedColumns(new Set(availableColumns.map(c => c.key)));
    }
    // Keep matching_tool mode if that's selected (per AC4)
    if (exportMode !== 'matching_tool') {
      setExportMode('custom');
    }
  };

  // Check if rate limited
  const isRateLimited = pendingCount >= maxPending;

  // Handle export submission
  const handleExport = async () => {
    if (selectedColumns.size === 0) {
      notifications.show({
        title: 'No columns selected',
        message: 'Please select at least one column to export',
        color: 'orange',
      });
      return;
    }

    setSubmitting(true);
    try {
      // Build filter context - add availability exclusion for matching tool exports
      let exportFilterContext = requestFilterContext;
      if (exportMode === 'matching_tool') {
        exportFilterContext = {
          ...(exportFilterContext || {}),
          exclude_missing_availability: !includeMissingAvailability,
          // MDF-208: only this mode writes the headers the matching sheet reads.
          matching_tool: true,
        };
      }

      // When gridColumns is provided, we always send an explicit selected_columns array
      // and skip the include_all_columns/match_current_view flags (MED-771).
      // This makes the frontend grid schema the single source of truth for column selection.
      const useGridSchema = gridColumns && (exportMode === 'all_columns' || exportMode === 'current_view');

      await exportsApi.create({
        entity_type: entityType,
        context_type: contextType,
        context_id: contextId,
        filter_context: exportFilterContext,
        selected_columns: Array.from(selectedColumns),
        include_all_columns: useGridSchema ? false : exportMode === 'all_columns',
        match_current_view: useGridSchema ? false : exportMode === 'current_view',
        format: selectedFormat as 'csv' | 'xlsx',
        ...(exportMode === 'matching_tool' && { populate_dnm_groupings: populateDnmGroupings }),
      });

      // Show appropriate toast message
      const missingCount = availabilityValidation?.missing_availability_count ?? 0;
      const totalCount = availabilityValidation?.total_participants ?? 0;
      if (exportMode === 'matching_tool' && missingCount > 0) {
        const toastMessage = includeMissingAvailability
          ? `Exporting all ${totalCount} participants (${missingCount} without availability data included with blank values). You will receive an email when ready.`
          : `Exported ${totalCount - missingCount} participants (${missingCount} excluded due to missing availability). You will receive an email when ready.`;
        notifications.show({
          title: 'Export requested',
          message: toastMessage,
          color: 'green',
          icon: <CheckCircle size={16} />,
          autoClose: 7000,
        });
      } else {
        notifications.show({
          title: 'Export requested',
          message: 'You will receive an email when your export is ready to download.',
          color: 'green',
          icon: <CheckCircle size={16} />,
          autoClose: 5000,
        });
      }

      onClose();
    } catch (error) {
      notifications.show({
        title: 'Export failed',
        message: error instanceof Error ? error.message : 'Failed to request export',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setExportMode('all_columns');
    setSelectedFormat('csv');
    setIncludeAllRecords(false);
    setAvailabilityValidation(null);
    setMissingListExpanded(false);
    setIncludeMissingAvailability(false);
    setPopulateDnmGroupings(true);
    setDnmValidation(null);
    setDnmValidationLoading(false);
    setDnmMissingListExpanded(false);
    onClose();
  };

  const entityLabel = entityLabelOverride ?? getEntityLabel(entityType);
  const hasActiveFilters = filterSummary.length > 0 || ignoredFilters.length > 0;
  // The Data Library sends contextType 'cohort' for one selected cohort, so its cohort list wins.
  const noticeScope: ExportScope = scopeContext?.cohortIds ? 'cohorts' : contextType === 'cohort' ? 'cohort' : 'all';
  const ignoredNotice = ignoredFiltersNotice(ignoredFilters, filterSummary.length, noticeScope);

  const modalTitle = exportTitle(entityLabel, recordCount, ignoredFilters.length === 0 && !dropFilters);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={modalTitle}
      size="lg"
      centered
    >
      <Box pos="relative">
        <LoadingOverlay visible={columnsLoading} />

        <Stack gap="lg">
          {/* Rate limit warning */}
          {isRateLimited && (
            <Alert icon={<AlertCircle size={16} />} color="orange" variant="light">
              You have {pendingCount} exports pending. Please wait for them to complete before requesting more.
            </Alert>
          )}

          {/* Export Mode Selection */}
          <Box>
            <Text size="sm" fw={500} mb="xs">
              Column Selection
            </Text>
            <Radio.Group
              value={exportMode}
              onChange={(value) => setExportMode(value as ExportMode)}
            >
              <Stack gap="sm">
                <Card
                  withBorder
                  padding="sm"
                  className={`${classes.modeCard} ${exportMode === 'all_columns' ? classes.modeCardSelected : ''}`}
                  onClick={() => setExportMode('all_columns')}
                >
                  <Group gap="sm" wrap="nowrap">
                    <Radio value="all_columns" />
                    <Box>
                      <Text fw={500} size="sm">All columns</Text>
                      <Text size="xs" c="dimmed">Export all available fields ({getAllColumnKeys(gridColumns, columns).length} columns)</Text>
                    </Box>
                  </Group>
                </Card>

                {currentViewColumns.length > 0 && (
                  <Card
                    withBorder
                    padding="sm"
                    className={`${classes.modeCard} ${exportMode === 'current_view' ? classes.modeCardSelected : ''}`}
                    onClick={() => setExportMode('current_view')}
                  >
                    <Group gap="sm" wrap="nowrap">
                      <Radio value="current_view" />
                      <Box>
                        <Text fw={500} size="sm">Match current view</Text>
                        <Text size="xs" c="dimmed">Export columns currently visible in the table ({gridColumns ? getVisibleExportKeys(gridColumns, currentViewColumns).length : currentViewColumns.length} columns)</Text>
                      </Box>
                    </Group>
                  </Card>
                )}

                {presets?.matching_tool && (
                  <Card
                    withBorder
                    padding="sm"
                    className={`${classes.modeCard} ${exportMode === 'matching_tool' ? classes.modeCardSelected : ''}`}
                    onClick={() => setExportMode('matching_tool')}
                  >
                    <Group gap="sm" wrap="nowrap">
                      <Radio value="matching_tool" />
                      <Box>
                        <Text fw={500} size="sm">{presets.matching_tool.label}</Text>
                        <Text size="xs" c="dimmed">{presets.matching_tool.description}</Text>
                      </Box>
                    </Group>
                  </Card>
                )}

                <Card
                  withBorder
                  padding="sm"
                  className={`${classes.modeCard} ${exportMode === 'custom' ? classes.modeCardSelected : ''}`}
                  onClick={() => setExportMode('custom')}
                >
                  <Group gap="sm" wrap="nowrap">
                    <Radio value="custom" />
                    <Box>
                      <Text fw={500} size="sm">Custom selection</Text>
                      <Text size="xs" c="dimmed">Choose specific columns to export</Text>
                    </Box>
                  </Group>
                </Card>
              </Stack>
            </Radio.Group>
          </Box>

          {/* Custom Column Selection (also shown for matching_tool to allow editing) */}
          {(exportMode === 'custom' || exportMode === 'matching_tool') && (
            <Box>
              <Box className={classes.selectAllRow}>
                <Text size="sm" fw={500}>
                  Select Columns ({selectedColumns.size} of {availableColumns.length})
                </Text>
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={toggleAll}
                >
                  {selectedColumns.size === availableColumns.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>

              <ScrollArea.Autosize mah={320} className={classes.columnsContainer}>
                <Stack gap="md">
                  {Object.entries(columnsByCategory).map(([category, categoryColumns]) => (
                    <Box key={category} className={classes.categorySection}>
                      <Box className={classes.categoryHeader}>
                        <Text className={classes.categoryLabel}>
                          {getCategoryLabel(category)}
                        </Text>
                        <Badge size="xs" variant="light" color="gray">
                          {categoryColumns.filter(c => selectedColumns.has(c.key)).length}/{categoryColumns.length}
                        </Badge>
                      </Box>
                      <Stack gap={4}>
                        {categoryColumns.map((column) => {
                          const locked = isColumnLocked(column.key);
                          const checkbox = (
                            <Checkbox
                              key={column.key}
                              label={
                                locked ? (
                                  <Group gap={6} wrap="nowrap">
                                    <span>{column.label}</span>
                                    <Lock size={12} style={{ color: 'var(--mantine-color-gray-5)', flexShrink: 0 }} />
                                  </Group>
                                ) : column.label
                              }
                              checked={selectedColumns.has(column.key)}
                              onChange={() => toggleColumn(column.key)}
                              disabled={locked}
                              className={classes.columnCheckbox}
                              size="sm"
                            />
                          );

                          return locked ? (
                            <Tooltip
                              key={column.key}
                              label="Required for matching tool"
                              position="right"
                              withArrow
                            >
                              <Box>{checkbox}</Box>
                            </Tooltip>
                          ) : (
                            checkbox
                          );
                        })}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            </Box>
          )}

          {/* Missing Availability Warning (matching tool only) */}
          {exportMode === 'matching_tool' && availabilityLoading && (
            <Alert icon={<AlertCircle size={16} />} color="gray" variant="light">
              <Text size="sm">Checking participant availability data...</Text>
            </Alert>
          )}
          {exportMode === 'matching_tool' && !availabilityLoading && availabilityValidation && availabilityValidation.missing_availability_count > 0 && (
            <Alert
              icon={<AlertCircle size={16} />}
              color="yellow"
              variant="light"
              title={`${availabilityValidation.missing_availability_count} participant(s) do not have availability data and will be excluded from export`}
            >
              <UnstyledButton
                onClick={() => setMissingListExpanded((prev) => !prev)}
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <Text size="xs" fw={500} c="yellow.8">
                  {missingListExpanded ? 'Hide' : 'Show'} affected participants
                </Text>
                {missingListExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </UnstyledButton>
              <Collapse in={missingListExpanded}>
                <Stack gap={2} mt="xs">
                  {availabilityValidation.missing_availability_participants.map((p, idx) => (
                    <Text key={idx} size="xs" c="dimmed">
                      {p.first_name} {p.last_name}
                    </Text>
                  ))}
                </Stack>
              </Collapse>
            </Alert>
          )}
          {exportMode === 'matching_tool' && !availabilityLoading && availabilityValidation && availabilityValidation.missing_availability_count > 0 && (
            <Checkbox
              label="Include participants without availability anyway"
              description="Participants without availability data will have blank values in availability columns"
              checked={includeMissingAvailability}
              onChange={(e) => setIncludeMissingAvailability(e.currentTarget.checked)}
              size="sm"
            />
          )}

          {/* DNM Groupings Settings (matching tool only) */}
          {exportMode === 'matching_tool' && (
            <Box>
              <Checkbox
                label="Populate DNM Groupings"
                description="Automatically calculate Do Not Match grouping numbers based on DNM email lists and shared managers"
                checked={populateDnmGroupings}
                onChange={(e) => setPopulateDnmGroupings(e.currentTarget.checked)}
                size="sm"
              />
              {populateDnmGroupings && dnmValidationLoading && (
                <Alert icon={<AlertCircle size={16} />} color="gray" variant="light" mt="xs">
                  <Text size="sm">Checking DNM groupings data...</Text>
                </Alert>
              )}
              {populateDnmGroupings && !dnmValidationLoading && dnmValidation && (
                <Stack gap="xs" mt="xs">
                  {dnmValidation.missing_manager_count > 0 && (
                    <Alert
                      icon={<AlertCircle size={16} />}
                      color="warningAmber"
                      variant="light"
                    >
                      <Text size="sm">
                        {dnmValidation.missing_manager_count} participant(s) are missing manager data. DNM groupings based on shared managers will be incomplete for these participants.
                      </Text>
                      <UnstyledButton
                        onClick={() => setDnmMissingListExpanded((prev) => !prev)}
                        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                        mt={4}
                      >
                        <Text size="xs" fw={500} c="warningAmber.8">
                          {dnmMissingListExpanded ? 'Hide' : 'Show'} affected participants
                        </Text>
                        {dnmMissingListExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </UnstyledButton>
                      <Collapse in={dnmMissingListExpanded}>
                        <Stack gap={2} mt="xs">
                          {dnmValidation.missing_manager_participants.map((p, idx) => (
                            <Text key={idx} size="xs" c="dimmed">
                              {p.first_name} {p.last_name}
                            </Text>
                          ))}
                        </Stack>
                      </Collapse>
                    </Alert>
                  )}
                  <Text size="xs" c="dimmed">
                    {dnmValidation.total_groups} DNM groups calculated for {dnmValidation.total_participants_with_groupings} of {dnmValidation.total_participants} participants
                  </Text>
                </Stack>
              )}
            </Box>
          )}

          {/* Format Selection */}
          <Box>
            <Text size="sm" fw={500} mb="xs">
              Export Format
            </Text>
            <Select
              data={[
                { value: 'csv', label: 'CSV (Comma Separated Values)' },
                { value: 'xlsx', label: 'Excel (.xlsx)' },
              ]}
              value={selectedFormat}
              onChange={(value) => setSelectedFormat(value || 'csv')}
              allowDeselect={false}
              w={280}
            />
          </Box>

          {/* Recipient Email */}
          {userEmail && (
            <Box>
              <Text size="sm" fw={500} mb="xs">
                Delivery
              </Text>
              <TextInput
                leftSection={<Mail size={16} />}
                value={userEmail}
                readOnly
                variant="filled"
                styles={{ input: { cursor: 'default' } }}
              />
              <Text size="xs" c="dimmed" mt={4}>
                Export will be emailed to this address when ready
              </Text>
            </Box>
          )}

          <Divider />

          {/* Filter Context */}
          {hasActiveFilters && (
            <Box>
              <Group gap="xs" mb="xs">
                <Filter size={14} />
                <Text size="sm" fw={500}>Active Filters</Text>
              </Group>

              <Box className={classes.filterSummary}>
                {filterSummary.length > 0 && (
                  <Group gap="xs" wrap="wrap">
                    {filterSummary.map((filter, idx) => (
                      <Box key={idx} className={classes.filterTag}>
                        <Text size="xs" fw={500}>{filter.label}:</Text>
                        <Text size="xs">{filter.value}</Text>
                      </Box>
                    ))}
                  </Group>
                )}

                {ignoredNotice && (
                  <Text size="xs" c="dimmed" mt={filterSummary.length > 0 ? 'sm' : 0}>
                    {ignoredNotice}
                  </Text>
                )}

                {filterSummary.length > 0 && (
                  <Checkbox
                    mt="sm"
                    label="Include all records (ignore current filters)"
                    checked={includeAllRecords}
                    onChange={(e) => setIncludeAllRecords(e.currentTarget.checked)}
                    size="sm"
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Info text */}
          <Text size="xs" c="dimmed">
            You will receive an email notification when your export is ready to download.
            Download links expire after 24 hours.
          </Text>

          {/* Actions */}
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              loading={submitting}
              disabled={isRateLimited || selectedColumns.size === 0}
              leftSection={<Download size={16} />}
            >
              Request Export
            </Button>
          </Group>
        </Stack>
      </Box>
    </Modal>
  );
}

export default ExportModal;
